import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAniList } from '../context/AniListContext';
import { RelatedSkeleton } from './common/SkeletonLoader';
import NewRelatedAnime from './NewRelatedAnime';
import RemovedAnimes from './RemovedAnimes';
import { getRemovedAnimeIds } from '../services/removedAnimeService';
import './RelatedAnime.css';

const RelatedAnime = () => {
    const { isRefreshing, isLoading, userData, pendingAnime } = useAniList();
    const [activeTab, setActiveTab] = useState('new');
    const [refreshKey, setRefreshKey] = useState(0);
    const [removedIdsVersion, setRemovedIdsVersion] = useState(0);

    // Compute removed count — must match what RemovedAnimes.js actually displays
    // Excludes stale entries (anime already in user's list)
    const removedCount = useMemo(() => {
        const removedIds = getRemovedAnimeIds();
        if (removedIds.size === 0) return 0;

        // Build set of user's own anime IDs to exclude stale removed entries
        const userAnimeIds = new Set();
        if (userData?.lists) {
            userData.lists.forEach(list => {
                (list.entries || []).forEach(e => {
                    if (e.media?.id) userAnimeIds.add(e.media.id);
                });
            });
        }

        // Count: removed from pending + missing (not stale) entries
        const fromPending = pendingAnime.filter(a => removedIds.has(a.id)).length;
        const missingCount = [...removedIds].filter(id =>
            !pendingAnime.some(a => a.id === id) && !userAnimeIds.has(id)
        ).length;
        return fromPending + missingCount;
    }, [pendingAnime, removedIdsVersion, userData]);

    // Compute new related count (pending minus removed)
    const newRelatedCount = useMemo(() => {
        const removedIds = getRemovedAnimeIds();
        return pendingAnime.filter(a => !removedIds.has(a.id)).length;
    }, [pendingAnime, removedIdsVersion]);

    // Separate view states for each tab
    const [newRelatedShowGrouped, setNewRelatedShowGrouped] = useState(true);
    const [removedShowGrouped, setRemovedShowGrouped] = useState(true);

    // Scroll position refs for each tab
    const newTabScrollRef = useRef(0);
    const removedTabScrollRef = useRef(0);
    const contentRef = useRef(null);

    // Callback to refresh both tabs when anime is removed or restored
    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
        setRemovedIdsVersion(prev => prev + 1);
    }, []);

    // Listen for removedAnimeChanged event for real-time count updates
    useEffect(() => {
        const handleRemovedChange = () => {
            setRemovedIdsVersion(prev => prev + 1);
        };
        window.addEventListener('removedAnimeChanged', handleRemovedChange);
        return () => window.removeEventListener('removedAnimeChanged', handleRemovedChange);
    }, []);

    // Save and restore scroll position when switching tabs
    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;

        // Restore scroll position for the active tab
        const savedScroll = activeTab === 'new' ? newTabScrollRef.current : removedTabScrollRef.current;
        container.scrollTop = savedScroll;

        // Save scroll position when user scrolls
        const handleScroll = () => {
            if (activeTab === 'new') {
                newTabScrollRef.current = container.scrollTop;
            } else {
                removedTabScrollRef.current = container.scrollTop;
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [activeTab]);

    // Show skeleton during initial load only
    if (isLoading && !userData) {
        return <RelatedSkeleton />;
    }

    return (
        <div className="discover-tabs-container">
            {/* Tab Navigation */}
            <div className="discover-tabs-nav">
                <button
                    className={`tab-nav-btn ${activeTab === 'new' ? 'active' : ''}`}
                    onClick={() => setActiveTab('new')}
                >
                    🔍 New Related Anime{newRelatedCount > 0 ? ` (${newRelatedCount})` : ''}
                </button>
                <button
                    className={`tab-nav-btn ${activeTab === 'removed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('removed')}
                >
                    🗑️ Removed Animes{removedCount > 0 ? ` (${removedCount})` : ''}
                </button>
            </div>

            {/* Tab Content */}
            <div className="discover-tabs-content" ref={contentRef}>
                {activeTab === 'new' ? (
                    <NewRelatedAnime
                        key={`new-${refreshKey}`}
                        onRefresh={handleRefresh}
                        showGrouped={newRelatedShowGrouped}
                        setShowGrouped={setNewRelatedShowGrouped}
                    />
                ) : (
                    <RemovedAnimes
                        key={`removed-${refreshKey}`}
                        onRefresh={handleRefresh}
                        showGrouped={removedShowGrouped}
                        setShowGrouped={setRemovedShowGrouped}
                    />
                )}
            </div>
        </div>
    );
};

export default RelatedAnime;
